import { useEffect, useMemo } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import {
  Sport,
  SubscribableType,
} from '@sorare/core/src/__generated__/globalTypes';
import { RadioGroup } from '@sorare/core/src/atoms/inputs/RadioGroup';
import { Accordion } from '@sorare/core/src/atoms/layout/Accordion';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useQueryState } from '@sorare/core/src/hooks/useQueryState';

const messages = defineMessages({
  all: {
    defaultMessage: 'All',

    selectedTypes: [
      SubscribableType.PLAYER,
      SubscribableType.TEAM,
      SubscribableType.COUNTRY,
      SubscribableType.BASEBALL_PLAYER,
      SubscribableType.NBA_PLAYER,
    ],
  },
  player: {
    defaultMessage: 'Player',

    selectedTypes: [
      SubscribableType.PLAYER,
      SubscribableType.BASEBALL_PLAYER,
      SubscribableType.NBA_PLAYER,
    ],
  },
  club: {
    defaultMessage: 'Club',
    selectedTypes: [SubscribableType.TEAM],
  },
  country: {
    defaultMessage: 'Country',
    selectedTypes: [SubscribableType.COUNTRY],
  },
});

const SubscribableTypeBySport: {
  [sport in Sport]: SubscribableType[];
} = {
  [Sport.FOOTBALL]: [
    SubscribableType.PLAYER,
    SubscribableType.TEAM,
    SubscribableType.COUNTRY,
  ],
  [Sport.BASEBALL]: [SubscribableType.BASEBALL_PLAYER],
  [Sport.NBA]: [SubscribableType.NBA_PLAYER],
};

type SelectType = keyof typeof messages;

type Props = {
  sportInputValue: Sport[];
};

export const useSubscribableTypesSelect = ({ sportInputValue }: Props) => {
  const [{ selectedType }, setSelectedType] = useQueryState({
    selectedType: 'all' as SelectType,
  });

  const { formatMessage } = useIntlContext();

  const selectedSubscribableTypesVariables: SubscribableType[] = useMemo(() => {
    return messages[selectedType].selectedTypes.filter(s => {
      return sportInputValue.some(sport =>
        SubscribableTypeBySport[sport].includes(s)
      );
    });
  }, [sportInputValue, selectedType]);

  const selectOptions = useMemo(() => {
    return Object.entries(messages)
      .filter(entries => {
        return sportInputValue.some(sport => {
          return entries[1].selectedTypes.find(s =>
            SubscribableTypeBySport[sport].includes(s)
          );
        });
      })
      .map(entries => ({
        label: formatMessage(entries[1]),
        value: entries[0] as SelectType,
      }));
  }, [formatMessage, sportInputValue]);

  useEffect(() => {
    if (!selectOptions.find(o => o.value === selectedType)) {
      setSelectedType({ selectedType: 'all' as SelectType });
    }
  }, [selectOptions, selectedType, setSelectedType]);
  return {
    subscribableTypeSelectNode: (
      <Accordion
        startsOpen
        title={
          <LabelM bold>
            <FormattedMessage defaultMessage="Type" />
          </LabelM>
        }
      >
        <RadioGroup
          name="subscribable-type-selector"
          options={selectOptions}
          value={selectedType}
          onChange={value => setSelectedType({ selectedType: value })}
        />
      </Accordion>
    ),
    selectedSubscribableTypesVariables,
  };
};
